import { calculateTextWidth } from 'src/components/scene/TextSprite/utils'
import type { TableCell, TableCellSettings, TableData, TableRow } from './types'

/**
 * Port of the backend plan table layout (`TableLayout` in
 * `server/src/md_core/model/export/plans/tableModel.py`) and of how the PDF
 * export draws a table (`make_table` in `functions/export/plans/pdf/pypdf.py`,
 * text placement in `pdf/free_text_appearance.py`). All values are PDF points;
 * the scene converts them with `ptToWorld` so the table renders exactly as the
 * exported plan. Change it only together with the export.
 */

/** `LINE_SPACING_RATIO` */
const LINE_SPACING_RATIO = 0.15

/** `label_line_height` — distance between two text lines of a cell, in em */
const CELL_LINE_HEIGHT = 1 + 2 * LINE_SPACING_RATIO

/** `ADDITIONAL_SCALING_NUM_CHARACTERS_THRESHOLD` — cells this short get the wider column */
const SHORT_TEXT_MAX_CHARS = 8

/** `textBlockWidth`'s default `maxTextWidthScalingFactor` (the export does not override it) */
const TEXT_WIDTH_SCALE = 1.15

/** width scaling of cells whose longest line is short */
const TEXT_WIDTH_SCALE_SHORT = 1.5

/** `calculate_text_width_arial`'s width for characters outside its glyph table, in em */
const UNKNOWN_GLYPH_WIDTH = 0.5

/**
 * `BLUEBEAM_TEXT_METRICS` (`pdf/bluebeam.py`): ascent and height of a text line
 * in em, used by every target viewer's appearance stream to place the text.
 */
const TEXT_ASCENT = 0.9123
const TEXT_LINE_BOX_HEIGHT = 1.1096

/** `TableCellSettings` class defaults, applied to a cell that brings its own settings */
const CELL_SETTINGS_CLASS_DEFAULTS: Required<Omit<TableCellSettings, 'backgroundColorHex'>> = {
  horizontalAlignment: 'center',
  verticalAlignment: 'middle',
  textColorHex: '000000',
}

/** `defaultTableCellSettings()` */
const DEFAULT_CELL_SETTINGS: ResolvedCellSettings = {
  horizontalAlignment: 'left',
  verticalAlignment: 'middle',
  textColorHex: '000000',
  backgroundColorHex: null,
}

/** `defaultHeaderCellSettings()` */
const DEFAULT_HEADER_CELL_SETTINGS: ResolvedCellSettings = {
  horizontalAlignment: 'center',
  verticalAlignment: 'middle',
  textColorHex: '000000',
  backgroundColorHex: 'F2F2F2',
}

/** `TableSettings` defaults */
const DEFAULT_BORDER_COLOR_HEX = '000000'
const DEFAULT_BORDER_WIDTH = 0.5
const DEFAULT_CELL_TEXT_MARGIN = 1.5

export interface ResolvedCellSettings {
  horizontalAlignment: 'left' | 'center' | 'right'
  verticalAlignment: 'top' | 'middle' | 'bottom'
  textColorHex: string
  backgroundColorHex: string | null
}

/** Axis-aligned rectangle in PDF points, origin at the table's bottom-left corner. */
export interface PdfRect {
  xMin: number
  yMin: number
  xMax: number
  yMax: number
}

/** One line of cell text, positioned by its baseline start. */
export interface PdfTableTextLine {
  text: string
  x: number
  baselineY: number
  colorHex: string
}

export interface PdfTableCell {
  rect: PdfRect
  settings: ResolvedCellSettings
  lines: PdfTableTextLine[]
}

export interface PdfTableLayout {
  /** table font size in points */
  fontSize: number
  totalWidth: number
  totalHeight: number
  /** cells in the export's drawing order: data rows top to bottom, then the header */
  cells: PdfTableCell[]
  verticalGridLineXs: number[]
  horizontalGridLineYs: number[]
  borderColorHex: string
  borderWidth: number
}

export interface TableLayoutMetrics {
  /** table font size in points (the plan's `fontSize`) */
  fontSize: number
  /** world meters per PDF point at the plan scale */
  ptToWorld: number
}

export interface TableLayoutDimensions {
  totalWidth: number
  totalHeight: number
}

const textWidth = (line: string, fontSize: number): number =>
  calculateTextWidth(line, fontSize, UNKNOWN_GLYPH_WIDTH)

/** `textBlockHeight` */
const textBlockHeight = (numLines: number, fontSize: number): number => {
  const lineCount = numLines <= 0 ? 1 : numLines
  return fontSize * lineCount + (lineCount + 1) * LINE_SPACING_RATIO * fontSize
}

/** `textBlockWidth` */
const textBlockWidth = (lines: string[], fontSize: number, cellTextMargin: number): number => {
  if (!lines.length) return 2 * cellTextMargin
  const maxNumCharacters = Math.max(...lines.map(line => line.length))
  const scale = maxNumCharacters <= SHORT_TEXT_MAX_CHARS ? TEXT_WIDTH_SCALE_SHORT : TEXT_WIDTH_SCALE
  const maxTextWidth = Math.max(...lines.map(line => textWidth(line, fontSize)))
  return maxTextWidth * scale + 2 * cellTextMargin
}

/** `cell.value or [""]` */
const cellLines = (cell: TableCell | undefined): string[] =>
  cell?.value && cell.value.length > 0 ? cell.value : ['']

const cellByColumn = (row: TableRow, columnKey: string): TableCell | undefined =>
  row.cells.find(cell => cell.columnKey === columnKey)

const withoutNullish = <T extends object>(value: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== null && entry !== undefined),
  ) as Partial<T>

/**
 * `TableData.resolveCellSettings`. A cell with its own settings overrides the
 * table defaults with the `TableCellSettings` class defaults for every field it
 * leaves out (the backend dumps the cell settings with their defaults); only a
 * missing background keeps the table default.
 */
export const resolveCellSettings = (
  table: TableData,
  cell: TableCell | undefined,
  isHeader: boolean,
): ResolvedCellSettings => {
  const tableDefaults = isHeader
    ? {
        ...DEFAULT_HEADER_CELL_SETTINGS,
        ...withoutNullish(table.settings.headerCellSettings ?? {}),
      }
    : { ...DEFAULT_CELL_SETTINGS, ...withoutNullish(table.settings.defaultCellSettings ?? {}) }
  if (!cell?.settings) return tableDefaults
  return {
    ...tableDefaults,
    ...CELL_SETTINGS_CLASS_DEFAULTS,
    ...withoutNullish(cell.settings),
  }
}

/** Start offsets of consecutive sizes (`_cumulativeOffsets`). */
const cumulativeOffsets = (sizes: number[]): number[] => {
  const offsets: number[] = []
  let offset = 0
  sizes.forEach(size => {
    offsets.push(offset)
    offset += size
  })
  return offsets
}

const sum = (sizes: number[]): number => sizes.reduce((total, size) => total + size, 0)

/**
 * Text lines of a cell placed like the PDF appearance stream
 * (`buildFreeTextAppearance` with `border_width=0`, `margin=cellTextMargin`).
 */
const placeCellText = (
  lines: string[],
  rect: PdfRect,
  settings: ResolvedCellSettings,
  fontSize: number,
  padding: number,
): PdfTableTextLine[] => {
  const width = rect.xMax - rect.xMin
  const height = rect.yMax - rect.yMin
  const lineHeight = CELL_LINE_HEIGHT * fontSize
  const blockHeight = (lines.length - 1) * lineHeight + TEXT_LINE_BOX_HEIGHT * fontSize
  const blockTop = {
    top: height - padding,
    middle: (height + blockHeight) / 2,
    bottom: padding + blockHeight,
  }[settings.verticalAlignment]

  const placed: PdfTableTextLine[] = []
  lines.forEach((text, index) => {
    if (!text) return
    const lineWidth = textWidth(text, fontSize)
    const x = {
      left: padding,
      center: (width - lineWidth) / 2,
      right: width - padding - lineWidth,
    }[settings.horizontalAlignment]
    placed.push({
      text,
      x: rect.xMin + x,
      baselineY: rect.yMin + blockTop - index * lineHeight - TEXT_ASCENT * fontSize,
      colorHex: settings.textColorHex,
    })
  })
  return placed
}

/**
 * Cell text margin, column widths and row heights (bottom to top) in points — the
 * sizing part of `TableLayout.fromTableData`, without placing any cell content.
 */
const computeTableGrid = (table: TableData, fontSize: number) => {
  const cellTextMargin = table.settings.cellTextMargin ?? DEFAULT_CELL_TEXT_MARGIN
  const measuredRows = table.headerRow ? [...table.rows, table.headerRow] : table.rows

  const columnWidths = table.columns.map(column =>
    Math.max(
      0,
      ...measuredRows.map(row =>
        textBlockWidth(cellLines(cellByColumn(row, column.key)), fontSize, cellTextMargin),
      ),
    ),
  )

  const rowHeight = (row: TableRow): number =>
    Math.max(
      0,
      ...table.columns.map(column => {
        const value = cellByColumn(row, column.key)?.value
        return textBlockHeight(value?.length ? value.length : 1, fontSize)
      }),
    ) +
    2 * cellTextMargin

  // `rowKeysBottomToTop`: data rows reversed, the header on top
  const rowsBottomToTop = [...table.rows].reverse()
  if (table.headerRow) rowsBottomToTop.push(table.headerRow)
  const rowHeights = rowsBottomToTop.map(rowHeight)

  return { cellTextMargin, columnWidths, rowsBottomToTop, rowHeights }
}

/**
 * Full PDF layout of a plan table in points, with its origin at the table's
 * bottom-left corner — `TableLayout.fromTableData` plus what `make_table` draws.
 */
export const computePdfTableLayout = (table: TableData, fontSize: number): PdfTableLayout => {
  const { cellTextMargin, columnWidths, rowsBottomToTop, rowHeights } = computeTableGrid(
    table,
    fontSize,
  )

  const columnOffsets = cumulativeOffsets(columnWidths)
  const rowOffsets = cumulativeOffsets(rowHeights)

  const rowRect = (rowIndexBottomToTop: number, columnIndex: number): PdfRect => ({
    xMin: columnOffsets[columnIndex],
    xMax: columnOffsets[columnIndex] + columnWidths[columnIndex],
    yMin: rowOffsets[rowIndexBottomToTop],
    yMax: rowOffsets[rowIndexBottomToTop] + rowHeights[rowIndexBottomToTop],
  })

  const totalWidth = sum(columnWidths)
  const totalHeight = sum(rowHeights)

  // drawing order of `make_table`: `table.rows`, then the header row
  const cells: PdfTableCell[] = []
  const drawnRows: [TableRow, boolean][] = table.rows.map(row => [row, false])
  if (table.headerRow) drawnRows.push([table.headerRow, true])
  drawnRows.forEach(([row, isHeader], drawIndex) => {
    const rowIndexBottomToTop = isHeader
      ? rowsBottomToTop.length - 1
      : table.rows.length - 1 - drawIndex
    table.columns.forEach((column, columnIndex) => {
      const cell = cellByColumn(row, column.key)
      const settings = resolveCellSettings(table, cell, isHeader)
      const rect = rowRect(rowIndexBottomToTop, columnIndex)
      cells.push({
        rect,
        settings,
        lines: placeCellText(cellLines(cell), rect, settings, fontSize, cellTextMargin),
      })
    })
  })

  // `verticalGridLineXs` / `horizontalGridLineYs`: every cell edge, the outer ones included
  const hasCells = columnWidths.length > 0 && rowHeights.length > 0
  const verticalGridLineXs = hasCells ? [...columnOffsets, totalWidth] : [0, totalWidth]
  const horizontalGridLineYs = hasCells ? [...rowOffsets, totalHeight] : [0, totalHeight]

  return {
    fontSize,
    totalWidth,
    totalHeight,
    cells,
    verticalGridLineXs,
    horizontalGridLineYs,
    borderColorHex: table.settings.borderColorHex ?? DEFAULT_BORDER_COLOR_HEX,
    borderWidth: table.settings.borderWidth ?? DEFAULT_BORDER_WIDTH,
  }
}

/** Table bounding-box size in scene world units, exactly as in the exported plan. */
export const computeTableLayoutDimensions = (
  table: TableData,
  { fontSize, ptToWorld }: TableLayoutMetrics,
): TableLayoutDimensions => {
  const { columnWidths, rowHeights } = computeTableGrid(table, fontSize)
  return { totalWidth: sum(columnWidths) * ptToWorld, totalHeight: sum(rowHeights) * ptToWorld }
}
